// Mining Crew data types and localStorage persistence + Supabase sync
import { syncCrewData, syncCrewMembership } from "./supabaseSync";
import { readLocalJson } from "./safeJson";

export interface CrewMember {
  id: string;
  name: string;
  avatar: string;
  contributed: number;
}

export interface CrewActivityItem {
  id: string;
  message: string;
  emoji: string;
  timestamp: string;
}

export interface CrewNotification {
  id: string;
  crewId: string;
  crewName: string;
  fromChildId: string;
  fromChildName: string;
  amount: number;
  message: string;
  timestamp: string;
  seenBy: string[]; // childIds who have seen this
}

export interface CrewGoal {
  icon: string;
  name: string;
  category: string;
  goldRequired: number;
  // Gold Store integration fields
  storeItemId?: string;
  soloCost?: number;
  crewDiscountPercent?: number;
  crewSize?: number;
  crewDescription?: string;
}

export type CrewStatus =
  | "waiting_members"
  | "pending_parent_approval"
  | "active"
  | "completed"
  | "denied";

export interface Crew {
  id: string;
  name: string;
  icon: string;
  creatorId: string;
  members: CrewMember[];
  goal: CrewGoal | null;
  totalGold: number;
  activity: CrewActivityItem[];
  completed: boolean;
  createdAt: string;
  notifications?: CrewNotification[];
  pastRewards?: CrewGoal[];
  rewardUnlocked?: boolean;
  /** Track which members approved the current goal (member IDs) */
  goalApprovals?: string[];
  /** Lifecycle status — single source of truth for activation gating */
  status?: CrewStatus;
  /** Snapshotted from the creator's parent config at creation time */
  parentApprovalRequired?: boolean;
  parentApprovedAt?: string;
  parentDeniedAt?: string;
  /** Total invited member count (creator + invites). Used to detect "all joined". */
  invitedMemberCount?: number;
}

export interface CrewInvite {
  id: string;
  crewId: string;
  crewName: string;
  crewIcon: string;
  fromChildId: string;
  fromChildName: string;
  toChildId: string;
  toChildName: string;
  status: "pending" | "accepted" | "declined";
  timestamp: string;
}

const CREWS_KEY = "screentime-crews";
const CREW_MEMBERSHIP_KEY = "screentime-crew-membership"; // childId -> crewId[]
const CREW_INVITES_KEY = "screentime-crew-invites";

function getAllCrews(): Record<string, Crew> {
  const raw = readLocalJson<Record<string, Crew>>(CREWS_KEY, {});
  // Migrate legacy crews (no `status` field) so the new gating logic doesn't
  // retroactively pause already-running crews. Pre-existing crews are assumed
  // to have been auto-started (legacy behaviour) — only new crews are gated.
  for (const crew of Object.values(raw)) {
    if (!crew.status) {
      crew.status = crew.completed ? "completed" : "active";
      // Legacy crews never went through the approval flow — mark as such so
      // the new flow doesn't try to gate them again on the next read.
      if (crew.parentApprovalRequired === undefined) crew.parentApprovalRequired = false;
    }
  }
  return raw;
}

function saveAllCrews(crews: Record<string, Crew>) {
  localStorage.setItem(CREWS_KEY, JSON.stringify(crews));
  // Sync each crew to DB
  for (const [crewId, crew] of Object.entries(crews)) {
    syncCrewData(crewId, crew);
  }
}

function getMemberships(): Record<string, string[]> {
  const parsed = readLocalJson<Record<string, unknown>>(CREW_MEMBERSHIP_KEY, {});
  if (Object.keys(parsed).length === 0) return {};
  // Migrate old format (string) to new format (string[])
  const result: Record<string, string[]> = {};
  for (const [key, val] of Object.entries(parsed)) {
    if (typeof val === "string") {
      result[key] = [val];
    } else if (Array.isArray(val)) {
      result[key] = val as string[];
    }
  }
  return result;
}

function saveMemberships(m: Record<string, string[]>) {
  localStorage.setItem(CREW_MEMBERSHIP_KEY, JSON.stringify(m));
  // Sync each membership to DB
  for (const [childId, crewIds] of Object.entries(m)) {
    syncCrewMembership(childId, crewIds);
  }
}

/** Get all crews a child belongs to */
export function getCrewsForChild(childId: string): Crew[] {
  const memberships = getMemberships();
  const crewIds = Array.from(new Set(memberships[childId] || []));
  const crews = getAllCrews();
  return crewIds.map((id) => crews[id]).filter(Boolean);
}

/** Legacy: get first crew for child */
export function getCrewForChild(childId: string): Crew | null {
  const crews = getCrewsForChild(childId);
  return crews.length > 0 ? crews[0] : null;
}

/** Read parent's `requireCrewApproval` flag for a given child without
 * importing store.ts (avoids circular dep). Falls back to false (auto-start). */
function readRequireCrewApproval(childId: string): boolean {
  try {
    const raw = localStorage.getItem("screentime-configs");
    if (!raw) return false;
    const all = JSON.parse(raw) as Record<string, { requireCrewApproval?: boolean }>;
    return !!all[childId]?.requireCrewApproval;
  } catch {
    return false;
  }
}

export function createCrew(name: string, icon: string, creatorId: string, creatorName: string, creatorAvatar: string): Crew {
  const parentApprovalRequired = readRequireCrewApproval(creatorId);
  const crew: Crew = {
    id: crypto.randomUUID(),
    name,
    icon,
    creatorId,
    members: [{ id: creatorId, name: creatorName, avatar: creatorAvatar, contributed: 0 }],
    goal: null,
    totalGold: 0,
    activity: [{
      id: crypto.randomUUID(),
      message: `${creatorName} started the crew`,
      emoji: "⛏️",
      timestamp: new Date().toISOString(),
    }],
    completed: false,
    rewardUnlocked: false,
    createdAt: new Date().toISOString(),
    status: "waiting_members",
    parentApprovalRequired,
    invitedMemberCount: 1,
  };
  const crews = getAllCrews();
  crews[crew.id] = crew;
  saveAllCrews(crews);
  const memberships = getMemberships();
  const existing = memberships[creatorId] || [];
  if (!existing.includes(crew.id)) existing.push(crew.id);
  memberships[creatorId] = existing;
  saveMemberships(memberships);
  return crew;
}

function isDuplicateCrewNotification(
  notifications: CrewNotification[] | undefined,
  candidate: Pick<CrewNotification, "crewName" | "fromChildId" | "fromChildName" | "amount" | "message" | "timestamp">,
) {
  const candidateTime = new Date(candidate.timestamp).getTime();

  return (notifications || []).some((notif) => {
    const notifTime = new Date(notif.timestamp).getTime();
    return (
      notif.crewName === candidate.crewName &&
      notif.fromChildId === candidate.fromChildId &&
      notif.fromChildName === candidate.fromChildName &&
      notif.amount === candidate.amount &&
      notif.message === candidate.message &&
      Math.abs(notifTime - candidateTime) < 5000
    );
  });
}

function getCrewNotificationDedupeKey(notif: CrewNotification) {
  const timeBucket = Math.floor(new Date(notif.timestamp).getTime() / 5000);
  return [
    notif.crewName,
    notif.fromChildId,
    notif.fromChildName,
    notif.amount,
    notif.message,
    timeBucket,
  ].join("::");
}

/** Compute the next status when membership/goal changes. */
function evaluateActivationStatus(crew: Crew): CrewStatus {
  // Already-finalized states stick.
  if (crew.status === "completed" || crew.status === "denied") return crew.status;
  // Already approved by parent — keep it active.
  if (crew.status === "active" && crew.parentApprovedAt) return "active";
  const pendingInvites = getPendingInvitesForCrew(crew.id);
  if (pendingInvites > 0) return "waiting_members";
  // All invited members are present (or solo crew).
  if (crew.parentApprovalRequired) {
    // Hard gate: never auto-activate when parent approval is required.
    return "pending_parent_approval";
  }
  return "active";
}

/** Re-evaluate and persist a crew's status. Safe to call from any mutation. */
export function refreshCrewStatus(crewId: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  const next = evaluateActivationStatus(crew);
  if (next !== crew.status) {
    crew.status = next;
    crews[crewId] = crew;
    saveAllCrews(crews);
  }
  return crew;
}

export function joinCrew(crewId: string, childId: string, childName: string, childAvatar: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  if (crew.members.find(m => m.id === childId)) return crew;
  crew.members.push({ id: childId, name: childName, avatar: childAvatar, contributed: 0 });
  // Auto-approve goal for new joiners (they joined knowing the goal)
  if (crew.goalApprovals !== undefined && !crew.goalApprovals.includes(childId)) {
    crew.goalApprovals.push(childId);
  }
  const joinedAt = new Date().toISOString();
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: `${childName} joined the crew`,
    emoji: "🎉",
    timestamp: joinedAt,
  });
  if (!crew.notifications) crew.notifications = [];
  const joinNotification: CrewNotification = {
    id: crypto.randomUUID(),
    crewId: crew.id,
    crewName: crew.name,
    fromChildId: childId,
    fromChildName: childName,
    amount: 0,
    message: `${childName} joined ${crew.name}!`,
    timestamp: joinedAt,
    seenBy: [childId],
  };
  if (!isDuplicateCrewNotification(crew.notifications, joinNotification)) {
    crew.notifications.push(joinNotification);
  }
  // Re-evaluate lifecycle status after the new member is added.
  const nextStatus = evaluateActivationStatus(crew);
  if (nextStatus !== crew.status) {
    crew.status = nextStatus;
    if (nextStatus === "active") {
      crew.activity.unshift({
        id: crypto.randomUUID(),
        message: "Crew is active — start contributing!",
        emoji: "🚀",
        timestamp: joinedAt,
      });
    } else if (nextStatus === "pending_parent_approval") {
      crew.activity.unshift({
        id: crypto.randomUUID(),
        message: "Waiting for parent approval",
        emoji: "🛡️",
        timestamp: joinedAt,
      });
    }
  }
  crews[crewId] = crew;
  saveAllCrews(crews);
  const memberships = getMemberships();
  const existing = memberships[childId] || [];
  if (!existing.includes(crewId)) existing.push(crewId);
  memberships[childId] = existing;
  saveMemberships(memberships);
  return crew;
}

export function setCrewGoal(crewId: string, goal: CrewGoal): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  crew.goal = goal;
  crew.rewardUnlocked = false;
  // Initialize goalApprovals - if only 1 member (new crew), auto-approve all
  if (crew.members.length <= 1) {
    crew.goalApprovals = crew.members.map(m => m.id);
  } else if (!crew.goalApprovals || crew.goalApprovals.length === 0) {
    // For restarted crews, approvals will be added via approveCrewGoal
    crew.goalApprovals = [];
  }
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: `Goal set: ${goal.name}`,
    emoji: "🎯",
    timestamp: new Date().toISOString(),
  });
  // Re-evaluate activation now that we have a goal — a solo crew with parent
  // approval required must move into pending_parent_approval immediately.
  const next = evaluateActivationStatus(crew);
  if (next !== crew.status) {
    crew.status = next;
    if (next === "pending_parent_approval") {
      crew.activity.unshift({
        id: crypto.randomUUID(),
        message: "Waiting for parent approval",
        emoji: "🛡️",
        timestamp: new Date().toISOString(),
      });
    }
  }
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

export function contributeGold(crewId: string, childId: string, childName: string, amount: number): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew || !crew.goal) return null;
  // Crew must be active before any gold can be contributed.
  if (crew.status && crew.status !== "active") return crew;
  const member = crew.members.find(m => m.id === childId);
  if (!member) return null;
  // Hard cap: never exceed goal
  const remaining = Math.max(0, crew.goal.goldRequired - crew.totalGold);
  if (remaining <= 0) return crew;
  const capped = Math.min(amount, remaining);
  const contributedAt = new Date().toISOString();
  member.contributed += capped;
  crew.totalGold += capped;
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: `${childName} added ${capped} gold`,
    emoji: "🪙",
    timestamp: contributedAt,
  });
  if (!crew.notifications) crew.notifications = [];
  const contributionNotification: CrewNotification = {
    id: crypto.randomUUID(),
    crewId: crew.id,
    crewName: crew.name,
    fromChildId: childId,
    fromChildName: childName,
    amount: capped,
    message: `${childName} added ${capped} gold to ${crew.name}!`,
    timestamp: contributedAt,
    seenBy: [childId],
  };
  if (!isDuplicateCrewNotification(crew.notifications, contributionNotification)) {
    crew.notifications.push(contributionNotification);
  }
  if (crew.goal && crew.totalGold >= crew.goal.goldRequired && !crew.completed) {
    // Only allow completion from an active crew.
    if (crew.status === "active" || !crew.status) {
      crew.completed = true;
      crew.status = "completed";
      crew.activity.unshift({
        id: crypto.randomUUID(),
        message: "Goal reached! 🎉",
        emoji: "🏆",
        timestamp: new Date().toISOString(),
      });
    }
  }
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Mark a completed crew reward as unlocked/claimed */
export function markCrewRewardUnlocked(crewId: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  crew.rewardUnlocked = true;
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Reset a completed crew for a new goal – keeps all members, resets gold & goal */
export function resetCrewForNewGoal(crewId: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  // Preserve the completed reward in history
  if (crew.goal) {
    if (!crew.pastRewards) crew.pastRewards = [];
    crew.pastRewards.push({ ...crew.goal });
  }
  crew.goal = null;
  crew.totalGold = 0;
  crew.completed = false;
  crew.rewardUnlocked = false;
  crew.goalApprovals = [];
  // Re-snapshot the latest parent setting on restart so future approval rules apply.
  crew.parentApprovalRequired = readRequireCrewApproval(crew.creatorId);
  crew.parentApprovedAt = undefined;
  crew.parentDeniedAt = undefined;
  crew.status = evaluateActivationStatus({ ...crew, status: undefined });
  crew.members = crew.members.map(m => ({ ...m, contributed: 0 }));
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: "New mission started!",
    emoji: "🚀",
    timestamp: new Date().toISOString(),
  });
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Approve a crew's current goal (called by each member) */
export function approveCrewGoal(crewId: string, childId: string, childName: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew || !crew.goal) return null;
  if (!crew.goalApprovals) crew.goalApprovals = [];
  if (!crew.goalApprovals.includes(childId)) {
    crew.goalApprovals.push(childId);
    crew.activity.unshift({
      id: crypto.randomUUID(),
      message: `${childName} approved the new reward!`,
      emoji: "✅",
      timestamp: new Date().toISOString(),
    });
  }
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Check if all crew members have approved the current goal */
export function isGoalFullyApproved(crew: Crew): boolean {
  if (!crew.goal) return false;
  // Backward compat: if no goalApprovals field exists, treat as fully approved
  if (crew.goalApprovals === undefined) return true;
  return crew.members.every(m => crew.goalApprovals!.includes(m.id));
}

export function leaveCrew(childId: string, crewId?: string): { refundAmount: number; crew: Crew | null } {
  let refundAmount = 0;
  let updatedCrew: Crew | null = null;

  if (crewId) {
    const crews = getAllCrews();
    const crew = crews[crewId];
    if (crew) {
      const member = crew.members.find(m => m.id === childId);
      refundAmount = member?.contributed || 0;
      const childName = member?.name || "Someone";
      const leftAt = new Date().toISOString();
      crew.members = crew.members.filter(m => m.id !== childId);
      crew.totalGold -= refundAmount;
      crew.activity.unshift({
        id: crypto.randomUUID(),
        message: `${childName} left the crew`,
        emoji: "👋",
        timestamp: leftAt,
      });
      if (!crew.notifications) crew.notifications = [];
      const leaveNotification: CrewNotification = {
        id: crypto.randomUUID(),
        crewId: crew.id,
        crewName: crew.name,
        fromChildId: childId,
        fromChildName: childName,
        amount: 0,
        message: `${childName} left ${crew.name}`,
        timestamp: leftAt,
        seenBy: [childId],
      };
      if (!isDuplicateCrewNotification(crew.notifications, leaveNotification)) {
        crew.notifications.push(leaveNotification);
      }
      if (crew.members.length === 0) {
        delete crews[crewId];
      } else {
        crews[crewId] = crew;
      }
      saveAllCrews(crews);
      updatedCrew = crew.members.length > 0 ? crew : null;
    }
  }

  const memberships = getMemberships();
  if (crewId) {
    const existing = memberships[childId] || [];
    memberships[childId] = existing.filter(id => id !== crewId);
    if (memberships[childId].length === 0) delete memberships[childId];
  } else {
    delete memberships[childId];
  }
  saveMemberships(memberships);
  return { refundAmount, crew: updatedCrew };
}

export function getCrewById(crewId: string): Crew | null {
  const crews = getAllCrews();
  return crews[crewId] || null;
}

// Get unseen crew notifications for a child
export function getUnseenCrewNotifications(childId: string): CrewNotification[] {
  return getAllCrewNotifications(childId).filter(n => !n.seenBy.includes(childId));
}

// Get ALL crew notifications for a child (excluding own contributions)
export function getAllCrewNotifications(childId: string): CrewNotification[] {
  const crews = getCrewsForChild(childId);
  const unique = new Map<string, CrewNotification>();

  for (const crew of crews) {
    for (const notif of crew.notifications || []) {
      if (notif.fromChildId === childId) continue;

      const dedupeKey = getCrewNotificationDedupeKey(notif);
      const existing = unique.get(dedupeKey);

      if (!existing) {
        unique.set(dedupeKey, notif);
        continue;
      }

      const mergedSeenBy = Array.from(new Set([...(existing.seenBy || []), ...(notif.seenBy || [])]));
      const newerNotification = new Date(notif.timestamp).getTime() >= new Date(existing.timestamp).getTime()
        ? notif
        : existing;

      unique.set(dedupeKey, {
        ...newerNotification,
        seenBy: mergedSeenBy,
      });
    }
  }

  return Array.from(unique.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

// Mark crew notifications as seen by a child
export function markCrewNotificationsSeen(childId: string) {
  const crews = getAllCrews();
  let changed = false;
  for (const crew of Object.values(crews)) {
    for (const notif of crew.notifications || []) {
      if (!notif.seenBy.includes(childId)) {
        notif.seenBy.push(childId);
        changed = true;
      }
    }
  }
  if (changed) saveAllCrews(crews);
}

/** Add a crew-wide notification when a parent approves/denies the goal */
export function addCrewGoalApprovalNotification(crewId: string, status: "approved" | "denied") {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return;

  if (!crew.notifications) crew.notifications = [];
  const message = status === "approved"
    ? `Crew goal "${crew.goal?.name || "Goal"}" has been approved!`
    : `Crew goal "${crew.goal?.name || "Goal"}" was not approved.`;
  const emoji = status === "approved" ? "✅" : "❌";

  const alreadyExists = crew.notifications.some(
    (notif) => notif.fromChildId === "__system__" && notif.message === message,
  );

  if (alreadyExists) return;

  crew.notifications.push({
    id: crypto.randomUUID(),
    crewId: crew.id,
    crewName: crew.name,
    fromChildId: "__system__",
    fromChildName: "Parent",
    amount: 0,
    message,
    timestamp: new Date().toISOString(),
    seenBy: [],
  });

  crew.activity.push({
    id: crypto.randomUUID(),
    message: `${emoji} Goal "${crew.goal?.name || "Goal"}" ${status}`,
    emoji,
    timestamp: new Date().toISOString(),
  });

  saveAllCrews(crews);
}


export const CREW_GOAL_CATEGORIES = [
  { id: "gaming", icon: "🎮", label: "Gaming Reward" },
  { id: "food", icon: "🍗", label: "Food Reward" },
  { id: "experience", icon: "🎢", label: "Experience" },
  { id: "surprise", icon: "🎁", label: "Big Surprise" },
];

export const CREW_GOAL_OPTIONS: Record<string, CrewGoal[]> = {
  gaming: [
    { icon: "🎮", name: "Gaming Night Bundle", category: "gaming", goldRequired: 2000 },
    { icon: "🕹️", name: "New Game Purchase", category: "gaming", goldRequired: 3000 },
    { icon: "🏆", name: "Gaming Tournament Entry", category: "gaming", goldRequired: 4000 },
  ],
  food: [
    { icon: "🍕", name: "Pizza Party", category: "food", goldRequired: 1500 },
    { icon: "🍔", name: "Restaurant Trip", category: "food", goldRequired: 2500 },
    { icon: "🎂", name: "Ultimate Feast", category: "food", goldRequired: 3500 },
  ],
  experience: [
    { icon: "🎢", name: "Theme Park Day", category: "experience", goldRequired: 3000 },
    { icon: "🎬", name: "Cinema Day Out", category: "experience", goldRequired: 2000 },
    { icon: "🏄", name: "Adventure Day", category: "experience", goldRequired: 4000 },
  ],
  surprise: [
    { icon: "🎁", name: "Mystery Box", category: "surprise", goldRequired: 2000 },
    { icon: "🌟", name: "Big Surprise Day", category: "surprise", goldRequired: 3500 },
    { icon: "🎉", name: "Ultimate Surprise", category: "surprise", goldRequired: 5000 },
  ],
};

export const CREW_ICONS = ["⛏️", "🔥", "⚡", "💎", "🚀", "🌟", "🏴‍☠️", "👻", "👑", "🤖"];

// ── Crew Invites ──

function getAllInvites(): CrewInvite[] {
  return readLocalJson<CrewInvite[]>(CREW_INVITES_KEY, []);
}

function saveAllInvites(invites: CrewInvite[]) {
  localStorage.setItem(CREW_INVITES_KEY, JSON.stringify(invites));
}

/** Send a crew invite to a child */
export function sendCrewInvite(
  crewId: string, crewName: string, crewIcon: string,
  fromChildId: string, fromChildName: string,
  toChildId: string, toChildName: string,
): CrewInvite | null {
  const invites = getAllInvites();
  // Check for duplicate pending invite
  const existing = invites.find(i =>
    i.crewId === crewId && i.toChildId === toChildId && i.status === "pending"
  );
  if (existing) return existing;

  const invite: CrewInvite = {
    id: crypto.randomUUID(),
    crewId, crewName, crewIcon,
    fromChildId, fromChildName,
    toChildId, toChildName,
    status: "pending",
    timestamp: new Date().toISOString(),
  };
  invites.push(invite);
  saveAllInvites(invites);
  // Track invited member count on the crew so we know when "all members joined".
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (crew) {
    crew.invitedMemberCount = (crew.invitedMemberCount ?? crew.members.length) + 1;
    // A pending invite means we're now waiting on members — back to waiting state
    // (unless the crew has already been approved/activated by parent).
    if (crew.status !== "completed" && crew.status !== "denied" && crew.status !== "active") {
      crew.status = "waiting_members";
    }
    crews[crewId] = crew;
    saveAllCrews(crews);
  }
  return invite;
}

/** Parent approves a pending crew → activate it. */
export function approveCrewByParent(crewId: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  if (crew.status !== "pending_parent_approval") return crew;
  crew.status = "active";
  crew.parentApprovedAt = new Date().toISOString();
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: "Parent approved — crew is active!",
    emoji: "✅",
    timestamp: crew.parentApprovedAt,
  });
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Parent denies a pending crew. Refunds any contributed gold (defensive — none expected at this stage). */
export function denyCrewByParent(crewId: string): Crew | null {
  const crews = getAllCrews();
  const crew = crews[crewId];
  if (!crew) return null;
  if (crew.status !== "pending_parent_approval") return crew;
  crew.status = "denied";
  crew.parentDeniedAt = new Date().toISOString();
  crew.activity.unshift({
    id: crypto.randomUUID(),
    message: "Parent did not approve this crew",
    emoji: "🛑",
    timestamp: crew.parentDeniedAt,
  });
  crews[crewId] = crew;
  saveAllCrews(crews);
  return crew;
}

/** Get pending crew invites for a child */
export function getPendingCrewInvites(childId: string): CrewInvite[] {
  return getAllInvites().filter(i => i.toChildId === childId && i.status === "pending");
}

/** Get handled (accepted/declined) crew invites for a child — for history */
export function getHandledCrewInvites(childId: string): CrewInvite[] {
  return getAllInvites().filter(i => i.toChildId === childId && (i.status === "accepted" || i.status === "declined"));
}

/** Accept a crew invite */
export function acceptCrewInvite(inviteId: string, childAvatar: string): Crew | null {
  const invites = getAllInvites();
  const invite = invites.find(i => i.id === inviteId);
  if (!invite || invite.status !== "pending") return null;
  invite.status = "accepted";
  saveAllInvites(invites);
  // Actually join the crew
  return joinCrew(invite.crewId, invite.toChildId, invite.toChildName, childAvatar);
}

/** Decline a crew invite */
export function declineCrewInvite(inviteId: string) {
  const invites = getAllInvites();
  const invite = invites.find(i => i.id === inviteId);
  if (!invite || invite.status !== "pending") return;
  invite.status = "declined";
  saveAllInvites(invites);
  // Decline can clear the last "waiting on someone" — re-evaluate the crew's status.
  const crews = getAllCrews();
  const crew = crews[invite.crewId];
  if (crew) {
    const next = evaluateActivationStatus(crew);
    if (next !== crew.status) {
      crew.status = next;
      crews[invite.crewId] = crew;
      saveAllCrews(crews);
    }
  }
}

/** Check if a child has a pending invite to a crew */
export function hasPendingInvite(crewId: string, childId: string): boolean {
  return getAllInvites().some(i => i.crewId === crewId && i.toChildId === childId && i.status === "pending");
}

/** Get count of pending invites for a specific crew */
export function getPendingInvitesForCrew(crewId: string): number {
  return getAllInvites().filter(i => i.crewId === crewId && i.status === "pending").length;
}
